import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Check, Link2, Lightbulb, Megaphone } from "lucide-react";
import { toast } from "sonner";

// Petits boutons de partage affichés en haut des pages publiques
const GMB_URL = "https://www.google.com/search?q=PROXIreduc-28+Eure-et-Loir";

function XIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.8 14.17c-.24.68-1.42 1.31-1.95 1.36-.5.05-1.13.07-1.83-.11-.42-.13-.96-.31-1.66-.61-2.92-1.26-4.83-4.2-4.98-4.39-.14-.19-1.18-1.57-1.18-3s.75-2.13 1.02-2.42c.27-.29.58-.36.78-.36l.56.01c.18.01.42-.07.66.5.24.58.82 2.01.89 2.16.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.3.37-.42.5-.14.14-.29.29-.12.57.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.39-.24.66-.14.27.09 1.7.8 1.99.95.29.14.48.22.55.34.07.12.07.68-.17 1.36Z" />
    </svg>
  );
}

function GoogleIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.83Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.29 14.3a7.2 7.2 0 0 1 0-4.6v-3.1H1.28a12 12 0 0 0 0 10.8l4.01-3.1Z" />
      <path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.44-3.44A11.96 11.96 0 0 0 12 0 12 12 0 0 0 1.28 6.6l4.01 3.1C6.23 6.86 8.88 4.75 12 4.75Z" />
    </svg>
  );
}

// Résout une URL de partage qui expose des balises Open Graph côté serveur,
// pour que Facebook/LinkedIn affichent un aperçu riche (titre + description + image)
// et pas seulement l'image ou le nom de domaine.
function shareUrlFor(pathname, backend) {
  const fallback = typeof window !== "undefined" ? window.location.href : "";
  if (!backend) return fallback;
  const p = pathname || "/";
  if (p === "/" || p === "") return `${backend}/api/share/accueil`;
  if (p.startsWith("/devenir-partenaire") || p.startsWith("/offre-fondateurs")) return `${backend}/api/share/devenir-partenaire`;
  if (p.startsWith("/organismes-collectivites")) return `${backend}/api/share/organismes`;
  if (p.startsWith("/pour-les-cse")) return `${backend}/api/share/cse`;
  let m;
  // Pages de détail (avec identifiant) : aperçu dédié à l'élément
  if ((m = p.match(/^\/blog\/([^/]+)/))) return `${backend}/api/blog-vitrine/${m[1]}`;
  if ((m = p.match(/^\/sortir\/([^/]+)/))) return `${backend}/api/event-vitrine/${m[1]}`;
  if ((m = p.match(/^\/offre\/([^/]+)/))) return `${backend}/api/offre-vitrine/${m[1]}`;
  if ((m = p.match(/^\/commerce\/([^/]+)/))) return `${backend}/api/vitrine/${m[1]}`;
  if ((m = p.match(/^\/petites-annonces\/annonce\/([^/]+)/))) return `${backend}/api/annonce-vitrine/${m[1]}`;
  // Pages de section : image de partage personnalisée par page
  if (p === "/blog") return `${backend}/api/share/blog`;
  if (p === "/sortir") return `${backend}/api/share/sortir`;
  if (p === "/fiches-google") return `${backend}/api/share/fiches-google`;
  if (p.startsWith("/autour-de-moi")) return `${backend}/api/share/autour-de-moi`;
  if (p.startsWith("/groupes-facebook")) return `${backend}/api/share/groupes-facebook`;
  if (p.startsWith("/carte")) return `${backend}/api/share/carte`;
  if (p.startsWith("/partenaires")) return `${backend}/api/share/partenaires`;
  if (p.startsWith("/bons-plans")) return `${backend}/api/share/bons-plans`;
  if (p.startsWith("/demande-de-devis")) return `${backend}/api/share/demande-de-devis`;
  if (p.startsWith("/entre-voisins")) return `${backend}/api/share/entre-voisins`;
  if (p.startsWith("/petites-annonces")) return `${backend}/api/share/petites-annonces`;
  return fallback || `${backend}/api/share/accueil`;
}

export default function ShareBar() {
  const [copied, setCopied] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const url = shareUrlFor(pathname, BACKEND);
  const text = "PROXIreduc-28 — Les bons plans près de chez vous en Eure-et-Loir";
  const e = encodeURIComponent;

  const links = [
    { key: "facebook", label: "Partager sur Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${e(url)}`, icon: <Facebook size={14} />, cls: "hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]" },
    { key: "whatsapp", label: "Partager sur WhatsApp", href: `https://wa.me/?text=${e(text + " " + url)}`, icon: <WhatsAppIcon />, cls: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]" },
    { key: "x", label: "Partager sur X", href: `https://twitter.com/intent/tweet?url=${e(url)}&text=${e(text)}`, icon: <XIcon />, cls: "hover:bg-black hover:text-white hover:border-black" },
    { key: "linkedin", label: "Partager sur LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(url)}`, icon: <Linkedin size={14} />, cls: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]" },
    { key: "gmb", label: "Voir sur Google", href: GMB_URL, icon: <GoogleIcon />, cls: "hover:bg-slate-50 hover:border-slate-300" },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  return (
    <div className="border-b border-slate-200/70 bg-white/60" data-testid="share-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-end gap-2">
        <Link to="/petites-annonces" data-testid="sharebar-petites-annonces"
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-[#E05638] hover:bg-orange-50 px-2 py-1 rounded-md transition-colors">
          <Megaphone size={13} /> Petites annonces
          <span className="text-[8px] font-bold text-white bg-[#E05638] rounded-full px-1 py-0.5 leading-none">Nouveau</span>
        </Link>
        <span className="mx-2 h-5 w-0.5 bg-slate-300 rounded-full" aria-hidden="true" data-testid="sharebar-annonces-divider" />
        <Link to="/suggestions" data-testid="sharebar-suggestions"
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-[#E05638] hover:bg-orange-50 px-2 py-1 rounded-md transition-colors">
          <Lightbulb size={13} /> Suggestions
        </Link>
        <span className="hidden sm:flex items-center gap-2">
          <span className="h-4 w-px bg-slate-300" aria-hidden="true" data-testid="sharebar-divider" />
          <span className="text-[11px] font-medium text-slate-500 mr-0.5">Partager&nbsp;:</span>
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={l.label}
              title={l.label}
              data-testid={`share-${l.key}`}
              className={`w-7 h-7 grid place-items-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors ${l.cls}`}
            >
              {l.icon}
            </a>
          ))}
          <button
            type="button"
            onClick={onCopy}
            aria-label="Copier le lien"
            title="Copier le lien"
            data-testid="share-copy"
            className="w-7 h-7 grid place-items-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-900 hover:text-white hover:border-slate-900"
          >
            {copied ? <Check size={14} /> : <Link2 size={14} />}
          </button>
        </span>
      </div>
    </div>
  );
}
